import { useCallback, useEffect } from 'react'
import { isAddress, zeroAddress } from 'viem'
import { useAccount, useContractReads, useNetwork } from 'wagmi'
import { getNetwork } from 'wagmi/actions'
import { useSwitchNetwork } from 'hooks/useSwitchNetwork'
import { competitionV3Abi, erc20ReadAbi, poolAbi } from './abi'
import { DEXES, V3_ADDRESS, dexByFactory } from './config'
import { useContractBinding } from './contract'

/** Connected wallet vs the one chain Competition V3 lives on (BNB Smart Chain). */
export function useV3Wallet() {
  // the chain of the contract the page is bound to (V3: BSC; V4: BSC, or BSC testnet during the testnet trial)
  const { chainId } = useContractBinding()
  const { chain } = useNetwork()
  const { address, isConnected } = useAccount()
  const { switchNetworkAsync, isLoading: switching } = useSwitchNetwork()
  const wrongChain = Boolean(isConnected && chain && chain.id !== chainId)
  const switchToBsc = useCallback(async () => {
    if (!isConnected || typeof switchNetworkAsync !== 'function') return false
    try {
      await switchNetworkAsync(chainId) // the app hook reports a refusal with a toast instead of throwing
      return getNetwork().chain?.id === chainId
    } catch {
      return false
    }
  }, [isConnected, switchNetworkAsync, chainId])
  return { address, isConnected, wrongChain, switching, switchToBsc, configured: Boolean(V3_ADDRESS) }
}

const onChain = (chainId: number) => (address: string | undefined, abi: any, functionName: string, args?: any[]) =>
  ({ address, abi, functionName, args, chainId }) as any

/** Symbol, decimals and (unless `light`) the contract's "listed" flag and minimum reward of any ERC-20, read on-chain. */
export function useTokenMeta(token?: string, light = false, contract: string = V3_ADDRESS) {
  const on = onChain(useContractBinding().chainId)
  const valid = Boolean(token && isAddress(token))
  const contracts = [
    on(token, erc20ReadAbi, 'symbol'),
    on(token, erc20ReadAbi, 'decimals'),
    ...(light
      ? []
      : [
          on(contract || undefined, competitionV3Abi, 'supportedRewardTokens', [token ?? zeroAddress]),
          on(contract || undefined, competitionV3Abi, 'minRewardAmount', [token ?? zeroAddress]),
        ]),
  ]
  const enabled = valid && Boolean(contract)
  const { data, isLoading, refetch } = useContractReads({
    contracts: contracts as any,
    enabled,
    // 30 s: a token the owner lists on-chain must stop showing as "Unverified" within seconds, not after the 5 minutes it used to be cached
    staleTime: 30_000,
    cacheTime: 30 * 60_000,
  }) as any
  // wagmi's query client is set to never refetch on window focus; this read does, so coming back to the tab after listing shows the new state at once
  useEffect(() => {
    if (light || !enabled) return undefined
    const again = () => {
      if (document.visibilityState === 'visible') refetch()
    }
    window.addEventListener('focus', again)
    document.addEventListener('visibilitychange', again)
    return () => {
      window.removeEventListener('focus', again)
      document.removeEventListener('visibilitychange', again)
    }
  }, [light, enabled, refetch])
  const symbol = data?.[0]?.status === 'success' ? (data[0].result as string) : undefined
  const decimals = data?.[1]?.status === 'success' ? Number(data[1].result) : undefined
  const listed = !light && data?.[2]?.status === 'success' ? Boolean(data[2].result) : undefined
  const minReward = !light && data?.[3]?.status === 'success' ? (data[3].result as bigint) : 0n
  // A contract that answers neither symbol nor decimals is not an ERC-20 the form can use.
  const notToken = valid && !isLoading && decimals === undefined
  return { valid, loading: valid && isLoading, symbol, decimals, listed, minReward, notToken }
}

/** Pool checks the contract repeats on-chain: known factory, and which two tokens it trades. */
export function usePoolInfo(pool?: string) {
  const on = onChain(useContractBinding().chainId)
  const valid = Boolean(pool && isAddress(pool))
  const { data, isLoading } = useContractReads({
    contracts: [on(pool, poolAbi, 'factory'), on(pool, poolAbi, 'token0'), on(pool, poolAbi, 'token1'), on(pool, poolAbi, 'fee')] as any,
    enabled: valid,
    staleTime: 5 * 60_000,
  }) as any
  const factory = data?.[0]?.status === 'success' ? (data[0].result as string) : undefined
  const token0 = data?.[1]?.status === 'success' ? (data[1].result as string) : undefined
  const token1 = data?.[2]?.status === 'success' ? (data[2].result as string) : undefined
  const fee = data?.[3]?.status === 'success' ? Number(data[3].result) : undefined
  const dex = dexByFactory(factory)
  return { valid, loading: valid && isLoading, factory, token0, token1, fee, dex, notPool: valid && !isLoading && !factory }
}

/** Fees and switches read straight from the contract (never a hard-coded price). */
export function useV3Settings(account?: string, referral?: string) {
  const on = onChain(useContractBinding().chainId)
  const { data, isLoading } = useContractReads({
    contracts: [
      on(V3_ADDRESS || undefined, competitionV3Abi, 'COST'),
      on(V3_ADDRESS || undefined, competitionV3Abi, 'COST_WITH_REFERRAL'),
      on(V3_ADDRESS || undefined, competitionV3Abi, 'paused'),
      on(V3_ADDRESS || undefined, competitionV3Abi, 'owner'),
      on(V3_ADDRESS || undefined, competitionV3Abi, 'currentValidatorConfigId'),
      on(V3_ADDRESS || undefined, competitionV3Abi, 'competitionRulesHash', [0n]),
      on(V3_ADDRESS || undefined, competitionV3Abi, 'competitionRulesHash', [1n]),
      on(V3_ADDRESS || undefined, competitionV3Abi, 'referralEnabled', [referral && isAddress(referral) ? referral : zeroAddress]),
      ...DEXES.map((d) => on(V3_ADDRESS || undefined, competitionV3Abi, 'supportedFactories', [d.factory])),
    ] as any,
    enabled: Boolean(V3_ADDRESS),
    staleTime: 30_000,
    watch: false,
  }) as any
  const val = (i: number) => (data?.[i]?.status === 'success' ? data[i].result : undefined)
  const owner = val(3) as string | undefined
  return {
    loading: isLoading,
    cost: val(0) as bigint | undefined,
    costWithReferral: val(1) as bigint | undefined,
    paused: val(2) as boolean | undefined,
    owner,
    isOwner: Boolean(account && owner && account.toLowerCase() === owner.toLowerCase()),
    configId: val(4) as bigint | undefined,
    rulesHash: [val(5), val(6)] as (`0x${string}` | undefined)[],
    referralEnabled: val(7) as boolean | undefined,
    factoryEnabled: DEXES.map((d, i) => ({ ...d, enabled: val(8 + i) as boolean | undefined })),
  }
}

/** DEX name and both token symbols of a competition pool, for cards and the detail page. */
export function usePoolPair(pool?: string) {
  const info = usePoolInfo(pool)
  const a = useTokenMeta(info.token0, true)
  const b = useTokenMeta(info.token1, true)
  return { ...info, symbol0: a.symbol, symbol1: b.symbol }
}

/** Symbols of many tokens in one multicall (for search on the list page). */
export function useSymbolMap(tokens: string[]) {
  const on = onChain(useContractBinding().chainId)
  const list = Array.from(new Set(tokens.filter((t) => isAddress(t))))
  const { data } = useContractReads({
    contracts: list.map((t) => on(t, erc20ReadAbi, 'symbol')) as any,
    enabled: list.length > 0,
    staleTime: 5 * 60_000,
  }) as any
  const map: Record<string, string> = {}
  list.forEach((t, i) => {
    if (data?.[i]?.status === 'success') map[t.toLowerCase()] = String(data[i].result)
  })
  return map
}
